import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { CITIES } from "@/data/content";

const navLinkBase =
  "text-xs tracking-[0.2em] uppercase font-semibold text-[#111] hover:text-[#555] transition-colors";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [citiesOpen, setCitiesOpen] = useState(false);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-black/5"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            data-testid="header-logo"
            className="font-serif-display text-2xl tracking-tight font-medium"
          >
            <span className="font-serif-display">Lex</span>
            <span className="text-[#065F46]">Work</span>
            <span className="text-[#1E3A8A]">space</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-10">
            <NavLink
              to="/stenopacemaster"
              data-testid="nav-stenospace"
              className={navLinkBase}
            >
              Stenography
            </NavLink>
            <NavLink
              to="/draftmaster"
              data-testid="nav-draftmaster"
              className={navLinkBase}
            >
              Legal AI
            </NavLink>

            <div
              className="relative"
              onMouseEnter={() => setCitiesOpen(true)}
              onMouseLeave={() => setCitiesOpen(false)}
            >
              <button
                data-testid="nav-cities-toggle"
                className={navLinkBase + " flex items-center gap-1"}
                onClick={() => setCitiesOpen((v) => !v)}
                type="button"
              >
                Cities
              </button>
              {citiesOpen && (
                <div
                  data-testid="nav-cities-menu"
                  className="absolute top-full left-1/2 -translate-x-1/2 mt-3 bg-white border border-black/10 shadow-xl w-80 p-6"
                >
                  <p className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-3">
                    Steno
                  </p>
                  <div className="grid grid-cols-2 gap-2 mb-5">
                    {CITIES.map((c) => (
                      <Link
                        key={"s-" + c.slug}
                        to={`/steno/${c.slug}`}
                        data-testid={`nav-city-steno-${c.slug}`}
                        className="text-sm hover:underline"
                      >
                        {c.short}
                      </Link>
                    ))}
                  </div>
                  <p className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-3">
                    Legal AI
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {CITIES.map((c) => (
                      <Link
                        key={"l-" + c.slug}
                        to={`/legal-ai/${c.slug}`}
                        data-testid={`nav-city-legal-${c.slug}`}
                        className="text-sm hover:underline"
                      >
                        {c.short}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <NavLink to="/blog" data-testid="nav-blog" className={navLinkBase}>
              Insights
            </NavLink>
            <NavLink to="/tools" data-testid="nav-tools" className={navLinkBase}>
              Tools
            </NavLink>
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href="https://stenopacemaster.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-cta-steno"
              className="btn-sharp bg-[#1E3A8A] text-white hover:bg-[#1D4ED8]"
            >
              Try Steno
            </a>
            <a
              href="https://draftmaster.ai"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-cta-draft"
              className="btn-sharp bg-[#065F46] text-white hover:bg-[#047857]"
            >
              Try DraftMaster
            </a>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle navigation"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {open && (
          <div
            data-testid="mobile-menu"
            className="lg:hidden pb-6 pt-2 border-t border-black/10 -mt-px"
          >
            <div className="flex flex-col gap-4 pt-4">
              <NavLink
                onClick={() => setOpen(false)}
                to="/stenopacemaster"
                className={navLinkBase}
              >
                Stenography
              </NavLink>
              <NavLink
                onClick={() => setOpen(false)}
                to="/draftmaster"
                className={navLinkBase}
              >
                Legal AI
              </NavLink>
              <NavLink
                onClick={() => setOpen(false)}
                to="/blog"
                className={navLinkBase}
              >
                Insights
              </NavLink>
              <NavLink
                onClick={() => setOpen(false)}
                to="/tools"
                className={navLinkBase}
              >
                Tools
              </NavLink>
              <div className="grid grid-cols-2 gap-3 pt-3">
                <a
                  href="https://stenopacemaster.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-sharp bg-[#1E3A8A] text-white text-center"
                >
                  Try Steno
                </a>
                <a
                  href="https://draftmaster.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-sharp bg-[#065F46] text-white text-center"
                >
                  Try DraftMaster
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
